/**
 * ARCHX AI — 3D layer loaders for the in-portal case viewer.
 *
 * Thin wrappers over three's STL/PLY loaders plus a MicronMapper photogrammetry
 * CSV parser (ported from engine/pipeline/photogrammetry_csv.py) that turns
 * implant positions + axes into 3D "rod" geometry.
 */
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';

/** File extension (lowercased, no dot) from a filename or path. */
export function extOf(nameOrPath = '') {
  const m = String(nameOrPath).toLowerCase().match(/\.([a-z0-9]+)(?:\?|$)/);
  return m ? m[1] : '';
}

/**
 * Fetch + parse a mesh file into a THREE.Mesh with a BufferGeometry.
 * Handles both binary/ascii STL and PLY. Material is assigned later by
 * SceneManager.addLayer, so the mesh comes back with the loader default.
 */
export async function loadMeshFromURL(url, ext) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetch failed (${res.status})`);
  const buf = await res.arrayBuffer();

  let geom;
  if (ext === 'ply') {
    geom = new PLYLoader().parse(buf);
  } else {
    // STLLoader.parse accepts an ArrayBuffer for binary and ascii STL alike.
    geom = new STLLoader().parse(buf);
  }
  if (!geom.attributes.normal) geom.computeVertexNormals();
  geom.computeBoundingBox();
  return new THREE.Mesh(geom);
}

/**
 * Parse MicronMapper photogrammetry CSV text → implants.
 * Row: AGN<id>, x, y, z, ax, ay, az, [bx, by, bz]
 * Mirrors photogrammetry_csv.parse_micronmapper_csv: normalise the axis and
 * flip it occlusal-up (+Z) for a consistent convention.
 * @returns {Array<{id:string, pos:number[], axis:number[]}>}
 */
export function parseImplantCSV(text) {
  const out = [];
  String(text).replace(/^﻿/, '').split(/\r?\n/).forEach((line) => {
    const m = line.match(/^\s*(AGN\d+)\s*,(.+)$/);
    if (!m) return;
    const v = m[2].split(',').map((s) => parseFloat(s.trim())).filter((x) => !Number.isNaN(x));
    if (v.length < 6) return;
    let ax = [v[3], v[4], v[5]];
    const n = Math.hypot(ax[0], ax[1], ax[2]);
    if (!n) return;
    ax = ax.map((c) => c / n);
    if (ax[2] < 0) ax = ax.map((c) => -c); // occlusal-up convention
    out.push({ id: m[1], pos: [v[0], v[1], v[2]], axis: ax });
  });
  return out;
}

/** Green (0mm) → yellow (1mm) → red (≥2mm) for a per-implant deviation. */
export function deviationColor(mm) {
  const t = Math.max(0, Math.min((mm || 0) / 2, 1));
  const c = new THREE.Color();
  c.setHSL((120 * (1 - t)) / 360, 0.65, 0.5); // hue 120(green)→0(red)
  return c;
}

/**
 * Build deviation markers: a sphere at each implant position, colored by how far
 * that implant was placed from the wax-up's planned channel (registration
 * residual). Per-sphere materials are set here, so add this layer with
 * keepMaterials so SceneManager doesn't recolor it.
 */
export function buildDeviationMarkers(report, { radius = 1.3 } = {}) {
  const group = new THREE.Group();
  (report.implants || []).forEach((im) => {
    const col = deviationColor(im.deviation_mm);
    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(radius, 20, 20),
      new THREE.MeshStandardMaterial({
        color: col, emissive: col.clone().multiplyScalar(0.25), roughness: 0.6,
      }),
    );
    const p = im.position || [0, 0, 0];
    mesh.position.set(p[0], p[1], p[2]);
    group.add(mesh);
  });
  return group;
}

/**
 * Build a THREE.Group of implant "rods": a cylinder along each implant axis
 * plus a small sphere marking the platform position. Material assigned later.
 */
export function buildImplantRods(implants, { length = 12, radius = 0.8 } = {}) {
  const group = new THREE.Group();
  const up = new THREE.Vector3(0, 1, 0); // CylinderGeometry default axis
  implants.forEach((im) => {
    const dir = new THREE.Vector3(im.axis[0], im.axis[1], im.axis[2]).normalize();
    const platform = new THREE.Vector3(im.pos[0], im.pos[1], im.pos[2]);

    const rod = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 16));
    rod.quaternion.copy(new THREE.Quaternion().setFromUnitVectors(up, dir));
    // center the rod half a length up the axis so it rises occlusally from the platform
    rod.position.copy(platform.clone().add(dir.clone().multiplyScalar(length / 2)));
    group.add(rod);

    const ball = new THREE.Mesh(new THREE.SphereGeometry(radius * 1.6, 16, 16));
    ball.position.copy(platform);
    group.add(ball);
  });
  return group;
}
